import React, { useEffect, useRef, useState } from 'react';
import SelectField from '../components/SelectField';
import {
  FaCheck,
  FaChevronDown,
  FaClipboardCheck,
  FaEnvelope,
  FaHistory,
  FaPen,
  FaSearch,
  FaTimes,
  FaUserFriends,
  FaUserPlus,
  FaUserTie,
  FaUsers
} from 'react-icons/fa';
import { InlineSpinner, TableRowsSkeleton } from '../components/LoadingState';
import { listCognitoUsers, listCognitoGroups } from '../api/users';
import {
  enableUser,
  disableUser,
  resetUserPassword,
  deleteUser,
  createUser,
  updateUser,
  setUserRole,
  setUserGroups
} from '../api/adminUsers';
import { saveUserEdits } from '../common/userEdits';
import { listAgentsWithEmails, updateAgentEmail, listActivityActors, getActorActivity, getMyTeam, setTeamMember } from '../api/agentEmails';
import ConfirmationModal from '../components/ConfirmationModal';
import ActionDropdown from '../components/ActionDropdown';
import Modal from '../components/Modal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import UserFormModal from '../components/UserFormModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import RoleModal from '../components/RoleModal';
import AgentEmailModal from '../components/AgentEmailModal';
import useDebounce from '../hooks/useDebounce';
import useIsSuperAdmin from '../hooks/useIsSuperAdmin';

// Search input with an inline clear button, used by both the Users and Agent Emails tabs.
function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative flex-1">
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        className="input-field !pl-10 w-full"
        style={{ paddingRight: value ? '2rem' : undefined }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          onClick={() => onChange('')}
        >
          <FaTimes style={{ fontSize: '0.75rem' }} />
        </button>
      )}
    </div>
  );
}

// Canonical CloudZA chip recipe with dark-safe tints.
const CHIP_BASE = 'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border';
const CHIP_TINTS = {
  cloudza: 'bg-cloudza-50 text-cloudza-700 border-cloudza-100',
  coral: 'bg-coral-50 text-coral-700 border-coral-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-gray-600 border-gray-200',
};

function Chip({ tint = 'gray', className = '', title, children }) {
  return (
    <span className={`${CHIP_BASE} ${CHIP_TINTS[tint] || CHIP_TINTS.gray} ${className}`} title={title}>
      {children}
    </span>
  );
}

// Per-Cognito-group chip tints (anything unknown falls back to gray).
const GROUP_TINT = {
  SuperAdmin: 'cloudza',
  Admin: 'coral',
  QAAgent: 'amber',
  CallAgent: 'gray',
};

// Identity tile with the initials of up to the first two words of a name.
const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/[\s\-_]+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('') || '?';

function IdentityTile({ name }) {
  return (
    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-xs font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
      {initialsOf(name)}
    </span>
  );
}

const ROLE_META = {
  GM: { label: 'General Manager', icon: FaUserTie, tint: 'cloudza' },
  JM: { label: 'Junior Manager', icon: FaUserFriends, tint: 'amber' },
  QA: { label: 'QA Assessor', icon: FaClipboardCheck, tint: 'coral' },
};

// Older GM rows store the role as "General Manager" rather than "GM".
const roleMeta = (role) => (role === 'General Manager' ? ROLE_META.GM : role && ROLE_META[role]);

function RoleBadge({ role }) {
  const meta = roleMeta(role);
  if (!meta) {
    return <Chip tint="gray">No Role</Chip>;
  }
  const Icon = meta.icon;
  return (
    <Chip tint={meta.tint} className="gap-1.5 whitespace-nowrap">
      <Icon style={{ fontSize: '0.7rem' }} />
      {meta.label}
    </Chip>
  );
}

// The loaded agents with one saved agent's details applied: their own row,
// and the "Team" list on any loaded GM row they joined or left.
function applySavedAgent(agents, saved) {
  return agents.map((agent) => {
    if (agent.name === saved.name) {
      return {
        ...agent,
        email: saved.email,
        role: saved.role,
        attachedGM: saved.attachedGM,
        updatedAt: saved.updatedAt,
        // A changed link changes who signs in as this agent. After an unlink a login matching the
        // agent's own name may still apply; the next load shows it.
        ...('linkedLogin' in saved && {
          linkedLogin: saved.linkedLogin,
          hasLogin: Boolean(saved.linkedLogin),
          loginUsername: saved.linkedLogin,
          loginEmail: null,
        }),
      };
    }
    const team = agent['attached-jm'] || [];
    const others = team.filter((member) => member.username !== saved.name);
    const joins = agent.name === saved.attachedGM;
    if (!joins && others.length === team.length) return agent;
    return {
      ...agent,
      'attached-jm': joins
        ? [...others, { username: saved.name, email: saved.email || 'N/A', date: saved.updatedAt }]
        : others,
    };
  });
}

// What the confirmation says after a save: every detail that was saved.
function describeSavedAgent(saved) {
  const { name, email, role, attachedGM } = saved;
  const meta = roleMeta(role);
  const login = !('linkedLogin' in saved)
    ? null
    : saved.linkedLogin
      ? `linked to the login ${saved.linkedLogin}`
      : 'no linked login';
  return [
    `${name} saved: ${meta ? meta.label : 'no role'}`,
    attachedGM ? `reports to ${attachedGM}` : null,
    email ? `email ${email}` : 'no email',
    login,
  ].filter(Boolean).join(', ') + '.';
}

// Agents per page / per Load more on the Agents tab.
// 30 (user, 2026-10-20). The server reads one DynamoDB page of this size per click; it caps at 100.
export const AGENT_PAGE_SIZE = 30;

// What a General Manager can do with one agent row: add an agent with no GM to their team, or take
// their own member off it. Another GM's member, a GM, and the GM themselves are left alone, as the
// server requires; a Junior Manager needs an admin to move them.
function TeamAction({ agent, me, busy, onChange }) {
  const isGm = agent.role === 'GM' || agent.role === 'General Manager';
  if (isGm || agent.name === me) return null;
  if (agent.attachedGM === me) {
    const isJm = agent.role === 'JM';
    return (
      <button
        onClick={() => onChange(agent, false)}
        disabled={busy || isJm}
        title={isJm ? 'A Junior Manager needs a General Manager; ask an admin to move them' : undefined}
        className="btn-outline !py-1 !px-3 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Remove from my team
      </button>
    );
  }
  if (agent.attachedGM) {
    return <span className="text-xs text-gray-400">On {agent.attachedGM}'s team</span>;
  }
  return (
    <button
      onClick={() => onChange(agent, true)}
      disabled={busy}
      className="btn-primary !py-1 !px-3 !text-xs disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Add to my team
    </button>
  );
}

// The Create User form starts empty; one object, so it is not reset on every render.
const NEW_USER = {};

// The "All General Managers" choice in the By General Manager picker; not an
// agent name (those come from the server).
const ALL_GMS = '__all_general_managers__';

// The Agents tab's role and login filter. Browser-only for now (user, 2026-10-20):
// it narrows the agents already loaded, not all of them, so Load more brings in
// further matches. "Login" means the agent's email belongs to a console user
// this page has loaded, the same rule as the "Cognito User Active" chip.
const AGENT_FILTERS = {
  all: () => true,
  gm: (agent) => agent.role === 'GM' || agent.role === 'General Manager',
  jm: (agent) => agent.role === 'JM',
  norole: (agent) => !agent.role,
  nologin: (agent, hasLogin) => !hasLogin,
  haslogin: (agent, hasLogin) => hasLogin,
  // The team itself is filtered on the server (`gm`), across every agent.
  bygm: () => true,
};

// One component, two pages: /users renders only the Cognito-users section, and
// /agents (via the AgentManagement wrapper below) renders the agent-email and
// activity-log sections. They stay in one file because they share the modals,
// the prefill flow (create a console user from an agent's email), and the
// lookup of existing user emails.
const UserManagement = ({ setAlert, userEmail, section = 'users' }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(section === 'agents' ? 'agents' : 'users');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [paginationToken, setPaginationToken] = useState();
  const [showCreate, setShowCreate] = useState(false);
  const [sortField, setSortField] = useState('Username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '' });
  const [editUserModal, setEditUserModal] = useState(null);
  const [resetUserModal, setResetUserModal] = useState(null);
  const [roleModal, setRoleModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');

  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const debouncedAgentSearch = useDebounce(agentSearch, 300);
  const [agentFilter, setAgentFilter] = useState('all');
  // The server searches, filters, sorts and counts every agent and sends one
  // page; Load more asks for the next from the `next` cursor.
  const [agentsNext, setAgentsNext] = useState(null);
  const [agentsLoadingMore, setAgentsLoadingMore] = useState(false);
  // Every GM, fetched from the server only when "By General Manager" is
  // chosen or the edit modal opens - never on load.
  const [generalManagers, setGeneralManagers] = useState([]);
  const [gmsStatus, setGmsStatus] = useState('idle'); // idle | loading | loaded | error
  const [agentGm, setAgentGm] = useState('');
  // Call Agent logins not tied to any calls, for the edit window's Login link. Fetched when the
  // edit window opens - it reads every login - and again after a save that changed a link.
  const [linkableLogins, setLinkableLogins] = useState([]);
  const [loginsStatus, setLoginsStatus] = useState('idle'); // idle | loading | loaded | error
  // Load more repeats the query of the page on screen; a newer query drops
  // any page still in flight.
  const agentPageQuery = useRef(null);
  const agentRun = useRef(0);
  const [editAgent, setEditAgent] = useState(null);

  // Activity log state
  const [actors, setActors] = useState([]);
  const [actorsLoading, setActorsLoading] = useState(false);
  const [selectedActor, setSelectedActor] = useState('');
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Console users and groups come from /auth, which is SuperAdmin-only. The Users page is theirs; the
  // Agents page asks only for a SuperAdmin (General Managers open it too).
  const isSAdmin = useIsSuperAdmin();
  const loadsConsoleUsers = section === 'users' || isSAdmin;

  // General Manager mode (2026-10-20): anyone else on the Agents page is a call agent, and if they are
  // a General Manager they manage their own team here - add agents with no GM, remove their members.
  // The server holds the rule; this only shapes the page.
  const isTeamMode = section === 'agents' && !isSAdmin;
  const [me, setMe] = useState({ status: 'idle', agentName: null, isGm: false }); // status: idle | loading | loaded | error
  const [teamScope, setTeamScope] = useState('mine'); // mine | unattached | all
  const [teamBusy, setTeamBusy] = useState(() => new Set()); // agents being added or removed

  useEffect(() => {
    if (!isTeamMode) return undefined;
    let cancelled = false;
    setMe((prev) => ({ ...prev, status: 'loading' }));
    getMyTeam()
      .then((team) => {
        // The server names the agent their calls are filed under; failing that, they are their username.
        if (!cancelled) setMe({ status: 'loaded', agentName: team.agentName || team.username, isGm: team.role === 'GM' });
      })
      .catch((err) => {
        console.error('Failed to load your team', err);
        if (!cancelled) setMe({ status: 'error', agentName: null, isGm: false });
      });
    return () => { cancelled = true; };
  }, [isTeamMode]);

  useEffect(() => {
    if (!loadsConsoleUsers) return undefined;
    let cancelled = false;
    loadUsers();
    (async () => {
      const grps = await listCognitoGroups();
      if (cancelled) return;
      setGroups(grps.groups || []);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (activeTab === 'activity' && actors.length === 0) {
      loadActors();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'agents') return;
    if (isTeamMode && !me.isGm) return; // not known to be a GM (yet): nothing to list
    loadAgents();
  }, [activeTab, debouncedAgentSearch, agentGm, teamScope, me.isGm]);

  useEffect(() => {
    if (editAgent) {
      loadGeneralManagers(); // the modal's GM dropdown, for attaching a Junior Manager
      loadLinkableLogins();
    }
  }, [editAgent]);

  useEffect(() => {
    if (!loadsConsoleUsers) return;
    setPage(1);
    loadUsers();
  }, [debouncedSearch, selectedGroup]);


  const normalizeUser = (u) => {
    if (u.Username && u.Attributes) {
      return {
        ...u,
        UserStatus: u.UserStatus || u.userStatus || u.status
      };
    }

    const normalGroups = (() => {
      if (!u.groups) return undefined;
      if (Array.isArray(u.groups)) {
        return u.groups
          .map(g => {
            if (typeof g === 'string') return g;
            if (typeof g === 'object' && g) {
              return g.groupName || g.name || g.GroupName || '';
            }
            return '';
          })
          .filter(Boolean)
          .join(',');
      }
      if (typeof u.groups === 'object') {
        return u.groups.groupName || u.groups.name || u.groups.GroupName || '';
      }
      return u.groups;
    })();

    return {
      Username: u.username || u.Username,
      Enabled: typeof u.enabled === 'boolean' ? u.enabled : true,
      UserStatus: u.UserStatus || u.userStatus || u.status,
      Attributes: [
        u.email ? { Name: 'email', Value: u.email } : null,
        normalGroups ? { Name: 'custom:groups', Value: normalGroups } : null,
      ].filter(Boolean),
    };
  };

  const loadUsers = async (tokenArg) => {
    setUsersLoading(true);
    try {
      const data = await listCognitoUsers(
        selectedGroup || null,
        debouncedSearch || null,
        tokenArg
      );
      let loaded = data.Users || data.users || data;
      if (Array.isArray(loaded)) {
        loaded = loaded.map(normalizeUser);
      } else if (loaded && Array.isArray(loaded.Users)) {
        loaded = loaded.Users.map(normalizeUser);
      } else {
        loaded = [];
      }
      if (tokenArg) {
        setUsers((prev) => [...prev, ...loaded]);
      } else {
        setUsers(loaded);
      }
      setPaginationToken(
        data.paginationToken || data.NextToken || data.nextToken || null
      );
    } catch (e) {
      console.error('Failed to load users', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load users" });
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAgents = async () => {
    const run = agentRun.current + 1;
    agentRun.current = run;
    const query = {
      count: AGENT_PAGE_SIZE,
      ...(debouncedAgentSearch.trim() && { q: debouncedAgentSearch.trim() }),
      ...(isTeamMode
        ? {
            ...(teamScope === 'mine' && { gm: me.agentName }),
            ...(teamScope === 'unattached' && { unattached: true }),
          }
        : {
            ...(agentGm === ALL_GMS && { role: 'gm' }),
            ...(agentGm && agentGm !== ALL_GMS && { gm: agentGm }),
          }),
    };
    setAgentsLoading(true);
    setAgentsLoadingMore(false); // a Load more for the old query no longer counts
    setAgentsNext(null); // nor does its cursor: Load more returns with the new first page
    try {
      const data = await listAgentsWithEmails(query);
      if (run !== agentRun.current) return;
      agentPageQuery.current = query;
      setAgents(data.agents || []);
      setAgentsNext(data.next || null);
    } catch (e) {
      console.error('Failed to load agents', e);
      if (run !== agentRun.current) return;
      // The rows on screen belong to the previous query, not the controls now
      // showing; Load more would carry on that old list.
      setAgents([]);
      setAgentsNext(null);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load agents" });
      }
    } finally {
      if (run === agentRun.current) setAgentsLoading(false);
    }
  };

  const loadGeneralManagers = async (force = false) => {
    if (!force && (gmsStatus === 'loading' || gmsStatus === 'loaded')) return;
    setGmsStatus('loading');
    try {
      const data = await listAgentsWithEmails({ managers: true });
      setGeneralManagers(data.generalManagers || []);
      setGmsStatus('loaded');
    } catch (e) {
      console.error('Failed to load general managers', e);
      setGmsStatus('error');
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load general managers" });
      }
    }
  };

  const loadLinkableLogins = async (force = false) => {
    if (!force && (loginsStatus === 'loading' || loginsStatus === 'loaded')) return;
    setLoginsStatus('loading');
    try {
      const data = await listAgentsWithEmails({ logins: true });
      setLinkableLogins(data.logins || []);
      setLoginsStatus('loaded');
    } catch (e) {
      console.error('Failed to load linkable logins', e);
      setLoginsStatus('error');
    }
  };

  const handleAgentFilterChange = (value) => {
    setAgentFilter(value);
    if (value === 'bygm') {
      loadGeneralManagers();
      setAgentGm(ALL_GMS); // every GM first, then narrow to one GM's team
    } else {
      setAgentGm(''); // leaving the team view goes back to every agent
    }
  };

  // A General Manager adds an agent to their team, or takes one off it; the row changes in place.
  const handleTeamChange = async (agent, join) => {
    setTeamBusy((prev) => new Set(prev).add(agent.name));
    try {
      await setTeamMember(agent.name, join);
      setAgents((prev) => prev.map((row) =>
        row.name === agent.name ? { ...row, attachedGM: join ? me.agentName : null } : row
      ));
    } catch (err) {
      console.error('Failed to change your team', err);
      if (setAlert) {
        setAlert({
          variant: 'error',
          heading: join ? "Couldn't add to your team" : "Couldn't remove from your team",
          text: err.message || 'Please try again.',
        });
      }
    } finally {
      setTeamBusy((prev) => {
        const next = new Set(prev);
        next.delete(agent.name);
        return next;
      });
    }
  };

  const loadMoreAgents = async () => {
    if (!agentsNext || agentsLoadingMore) return;
    const run = agentRun.current;
    setAgentsLoadingMore(true);
    try {
      const data = await listAgentsWithEmails({ ...agentPageQuery.current, after: agentsNext });
      if (run !== agentRun.current) return;
      setAgents((prev) => [...prev, ...(data.agents || [])]);
      setAgentsNext(data.next || null);
    } catch (e) {
      console.error('Failed to load more agents', e);
      if (run !== agentRun.current) return;
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load more agents" });
      }
    } finally {
      // A stale page clearing this would re-enable the button while a newer
      // request is out, and a second click would append its rows twice.
      if (run === agentRun.current) setAgentsLoadingMore(false);
    }
  };

  const loadActors = async () => {
    setActorsLoading(true);
    try {
      const data = await listActivityActors();
      setActors(data || []);
    } catch (e) {
      console.error('Failed to load activity actors', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load the activity log" });
      }
    } finally {
      setActorsLoading(false);
    }
  };

  const handleSelectActor = async (actorEmail) => {
    setSelectedActor(actorEmail);
    if (!actorEmail) {
      setActivities([]);
      return;
    }
    setActivitiesLoading(true);
    try {
      const data = await getActorActivity(actorEmail);
      setActivities(data.activities || []);
    } catch (e) {
      console.error('Failed to load actor activity', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load this admin's activity" });
      }
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleUpdateAgentEmail = async (agentName, email, role, gmUsername, loginUsername) => {
    try {
      await updateAgentEmail(agentName, email, role, gmUsername, userEmail, loginUsername);
      setEditAgent(null);
      const saved = {
        name: agentName,
        email: email || null,
        role: role || null,
        attachedGM: gmUsername || null,
        updatedAt: new Date().toISOString(),
        // Left out of the save, the login link is unchanged (see AgentEmailModal).
        ...(loginUsername !== undefined && { linkedLogin: loginUsername || null }),
      };
      // Show what was saved in place. Reloading here read GSI1, which DynamoDB
      // updates a moment after the save, so the row often came back with the
      // old role - and the list jumped back to page one.
      setAgents((prev) => applySavedAgent(prev, saved));
      setConfirmation({
        open: true,
        title: 'Agent Updated',
        message: describeSavedAgent(saved),
      });
      if (gmsStatus !== 'idle') {
        loadGeneralManagers(true); // a role change can add or remove a GM
      }
      if (loginUsername !== undefined) {
        setLoginsStatus('idle'); // a link changed which logins are free; fetch again on next open
      }
    } catch (err) {
      console.error('Failed to update agent email', err);
      setConfirmation({
        open: true,
        title: 'Error',
        message: err.message || "Couldn't update agent email"
      });
    }
  };


  const existingUserEmails = new Set(
    users.map(u => 
      u.Attributes?.find(a => a.Name === 'email')?.Value?.toLowerCase()
    ).filter(Boolean)
  );
  // A sign-in exists either as a Call Agent user the server matched to this agent (hasLogin) or as a
  // loaded console user whose email matches the agent's.
  const canSignIn = (agent) =>
    Boolean(agent.hasLogin || (agent.email && existingUserEmails.has(agent.email.toLowerCase())));
  const visibleAgents = agents.filter((agent) => AGENT_FILTERS[agentFilter](agent, canSignIn(agent)));

  const handleEnable = (u) => {
    setConfirmAction({
      title: 'Enable User',
      message: `Enable ${u.Username}?`,
      onConfirm: async () => {
        await enableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };

  const handleDisable = (u) => {
    setConfirmAction({
      title: 'Disable User',
      message: `Disable ${u.Username}?`,
      onConfirm: async () => {
        await disableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };


  const handleReset = (u) => {
    setResetUserModal(u);
  };

  const handleDelete = (u) => {
    setDeleteUserName(u.Username);
  };

  const confirmDeleteUser = async () => {
    await deleteUser(deleteUserName);
    setDeleteUserName('');
    loadUsers();
  };

  const handleSetRole = (u) => {
    setRoleModal(u);
  };

  const handleEdit = (u) => {
    setEditUserModal(u);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (dir) => {
    setPage((p) => {
      const total = Math.ceil(sorted.length / PAGE_SIZE) || 1;
      const next = p + dir;
      if (next < 1) return 1;
      if (next > total) return total;
      return next;
    });
  };


  const filtered = users.filter(u =>
    u.Username.toLowerCase().includes(search.toLowerCase()) ||
    (u.Attributes?.find(a => a.Name === 'email')?.Value || '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal;
    let bVal;
    switch (sortField) {
      case 'Email':
        aVal = a.Attributes?.find(a => a.Name === 'email')?.Value || '';
        bVal = b.Attributes?.find(a => a.Name === 'email')?.Value || '';
        break;
      case 'Status':
        aVal = a.UserStatus;
        bVal = b.UserStatus;
        break;
      case 'Username':
      default:
        aVal = a.Username;
        bVal = b.Username;
    }
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const JMListDropdown = ({ jms }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
      <div className="relative inline-block text-left mt-1">
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click events
            setIsOpen(!isOpen);
          }}
          className="flex items-center text-[10px] font-medium text-cloudza-700 bg-cloudza-50 hover:bg-cloudza-100 px-2 py-0.5 rounded-full border border-cloudza-100 transition-colors"
        >
          Team: {jms.length}
          <FaChevronDown className={`ml-1 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ fontSize: '0.6rem' }} />
        </button>

        {isOpen && (
          <>
            {/* Transparent overlay to close when clicking outside */}
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
            
            <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-1">
              <div className="px-3 py-1 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Reports to this GM
              </div>
              <div className="max-h-32 overflow-y-auto">
                {jms.map((jm, idx) => (
                  <div key={idx} className="px-3 py-2 hover:bg-gray-50 flex flex-col border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-gray-800">{jm.username}</span>
                    <span className="text-[10px] text-gray-500">{jm.email}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{section === 'agents' ? 'Agent Management' : 'User Management'}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {section !== 'agents'
              ? 'Manage console users and their roles'
              : isTeamMode
                ? 'Add agents to your team and see who reports to you'
                : 'Manage agent emails, roles, hierarchy, and audit activity'}
          </p>
        </div>
      </div>

      {/* Tabs — only the agents page has more than one section (its own audit
          trail); the users page is single-purpose so it shows no tab bar. */}
      {section === 'agents' && !isTeamMode && (
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" data-tour="user-tabs">
            <button
              onClick={() => setActiveTab('agents')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'agents'
                  ? 'border-cloudza-600 text-cloudza-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'
              }`}
            >
              <FaUserTie />
              Agent Emails
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'activity'
                  ? 'border-cloudza-600 text-cloudza-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'
              }`}
            >
              <FaHistory />
              Activity Log
            </button>
          </nav>
        </div>
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full">
          <SearchInput value={search} onChange={setSearch} placeholder="Search users" />
          <SelectField
            className="select-field sm:w-48"
            value={selectedGroup}
            onChange={e => {
              // The [debouncedSearch, selectedGroup] effect refetches with the
              // new group - calling loadUsers() here too fired a second fetch
              // with the stale selectedGroup closure and raced the effect.
              setSelectedGroup(e.target.value);
              setPaginationToken(null);
            }}
          >
            <option value="">All Groups</option>
            {groups.map(g => (
              <option key={g.GroupName} value={g.GroupName}>{g.GroupName}</option>
            ))}
          </SelectField>
          <button onClick={() => setShowCreate(true)} className="btn-primary whitespace-nowrap"><FaUserPlus /> Create User</button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <th onClick={() => handleSort('Username')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">Username {sortField === 'Username' ? <span className="text-cloudza-600">{sortDirection === 'asc' ? '↑' : '↓'}</span> : ''}</th>
            <th onClick={() => handleSort('Email')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">Email {sortField === 'Email' ? <span className="text-cloudza-600">{sortDirection === 'asc' ? '↑' : '↓'}</span> : ''}</th>
            <th onClick={() => handleSort('Status')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">Status {sortField === 'Status' ? <span className="text-cloudza-600">{sortDirection === 'asc' ? '↑' : '↓'}</span> : ''}</th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Groups</th>
            <th className="px-5 py-2.5 bg-[#fafafc]" />
          </tr>
        </thead>
        {usersLoading ? (
          <tbody>
            <TableRowsSkeleton columns={5} />
          </tbody>
        ) : (
          <tbody className="bg-white divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-10 px-6 text-center">
                  <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                    <FaUsers className="text-lg" />
                  </span>
                  <p className="mt-4 text-sm font-semibold text-gray-800">No users found</p>
                  <p className="mt-1 text-sm text-gray-400">Adjust your search or group filter, or create a new user.</p>
                </td>
              </tr>
            ) : paginated.map(u => {
              const email = u.Attributes?.find(a => a.Name === 'email')?.Value || '';
              const groups = u.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '';
              const isDisabled = u.Enabled === false;
              const status = u.UserStatus === 'UNCONFIRMED' ? 'Unconfirmed' : (isDisabled ? 'Disabled' : 'Enabled');
              return (
                <tr key={u.Username} className="hover:bg-cloudza-50/40 transition-colors">
                  <td className="px-5 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <IdentityTile name={u.Username} />
                      <span className="font-semibold text-gray-900">{u.Username}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{email}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className={`status-badge ${status === 'Enabled' ? 'status-active' : status === 'Disabled' ? 'status-inactive' : 'status-pending'}`}>{status}</span>
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {groups ? (
                      <div className="flex flex-wrap gap-1">
                        {groups.split(',').map(g => g.trim()).filter(Boolean).map(g => (
                          <Chip key={g} tint={GROUP_TINT[g] || 'gray'}>{g}</Chip>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-sm">
                    <ActionDropdown
                      actions={[
                        isDisabled
                          ? { label: 'Enable', onClick: () => handleEnable(u) }
                          : { label: 'Disable', onClick: () => handleDisable(u) },
                        { label: 'Set Role', onClick: () => handleSetRole(u) },
                        { label: 'Edit', onClick: () => handleEdit(u) },
                        { label: 'Reset PW', onClick: () => handleReset(u) },
                        { label: 'Delete', onClick: () => handleDelete(u), className: 'text-red-600' },
                      ]}
                    />
                  </td>
                </tr>
            );
            })}
          </tbody>
        )}
      </table>
      </div>
      {paginationToken && (
        <div className="mt-4 text-center">
          <button onClick={() => loadUsers(paginationToken)} className="btn-outline"><FaChevronDown /> Load More</button>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 mt-4">
          <button onClick={() => handlePageChange(-1)} disabled={page === 1} className="btn-outline !py-1 !px-3 !text-xs">Prev</button>
          <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
          <button onClick={() => handlePageChange(1)} disabled={page >= totalPages} className="btn-outline !py-1 !px-3 !text-xs">Next</button>
        </div>
      )}
        </>
      )}

              {/* Agents Tab Content */}
      {activeTab === 'agents' && isTeamMode && me.status === 'loaded' && !me.isGm && (
        <div className="card text-sm text-gray-600">
          Agent Management is for administrators and General Managers.
        </div>
      )}
      {activeTab === 'agents' && isTeamMode && me.status === 'error' && (
        <div className="card text-sm text-gray-600">
          Couldn't check your team just now. Reload the page to try again.
        </div>
      )}

      {activeTab === 'agents' && (!isTeamMode || me.isGm) && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full">
            <SearchInput value={agentSearch} onChange={setAgentSearch} placeholder="Search agents by name or email" />
            {isTeamMode ? (
              <SelectField
                className="select-field sm:w-48"
                value={teamScope}
                onChange={(e) => setTeamScope(e.target.value)}
              >
                <option value="mine">My team</option>
                <option value="unattached">Without a GM</option>
                <option value="all">All agents</option>
              </SelectField>
            ) : (
            <SelectField
              className="select-field sm:w-48"
              value={agentFilter}
              onChange={(e) => handleAgentFilterChange(e.target.value)}
            >
              <option value="all">All Agents</option>
              <option value="bygm">By General Manager…</option>
              <option value="gm">General Managers</option>
              <option value="jm">Junior Managers</option>
              <option value="norole">No role</option>
              <option value="nologin">No login</option>
              <option value="haslogin">Has login</option>
            </SelectField>
            )}
            {!isTeamMode && agentFilter === 'bygm' && (
              <SelectField
                className="select-field sm:w-64"
                value={agentGm}
                onChange={(e) => setAgentGm(e.target.value)}
              >
                <option value={ALL_GMS}>All General Managers</option>
                {gmsStatus === 'loading' && (
                  <option value="" disabled>Loading general managers…</option>
                )}
                {generalManagers.map((gm) => (
                  <option key={gm.name} value={gm.name}>{gm.name}</option>
                ))}
              </SelectField>
            )}
          </div>

          {/* No totals: each Load more reads only the agents it shows, so there is
              nothing that has counted every agent (user, 2026-10-20). Most
              recently active agents come first. */}
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Agent Name</th>
                  {/* Added Role Column */}
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Role & Hierarchy</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Email Address</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Last Updated</th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Status</th>
                  <th className="px-5 py-2.5 bg-[#fafafc]" />
                </tr>
              </thead>
              {agentsLoading ? (
                <tbody>
                  <TableRowsSkeleton columns={6} />
                </tbody>
              ) : (
                <tbody className="bg-white divide-y divide-gray-100">
                  {visibleAgents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-10 px-6 text-center">
                        <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                          <FaUserTie className="text-lg" />
                        </span>
                        <p className="mt-4 text-sm font-semibold text-gray-800">No agents found</p>
                        <p className="mt-1 text-sm text-gray-400">Agents appear here once they have a processed call or a user with the Call Agent role. Try a different search or filter.</p>
                      </td>
                    </tr>
                  ) : visibleAgents.map((agent) => {
                    const isRegistered = canSignIn(agent);

                    return (
                      <tr key={agent.name} className="hover:bg-cloudza-50/40 transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <IdentityTile name={agent.name} />
                            <div className="flex flex-col">
                              <span className="font-semibold text-gray-900">{agent.name}</span>
                              {agent.hasLogin && agent.loginUsername && agent.loginUsername !== agent.name && (
                                <span className="text-[11px] text-gray-500">Signs in as {agent.loginUsername}</span>
                              )}
                            </div>
                            {!agent.hasLogin && (
                              <Chip tint="gray" className="whitespace-nowrap" title="No Call Agent user signs in as this agent, so nobody sees these calls under My Calls. Link one in Edit Details.">
                                No login
                              </Chip>
                            )}
                          </div>
                        </td>
                        
                        {/* Role & Hierarchy Cell */}
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex flex-col">
                            <RoleBadge role={agent.role} />

                            {/* Show Hierarchy Links */}
                            {agent.attachedGM && (
                              <span className="text-[10px] text-gray-500 mt-1">
                                Reports to: <b>{agent.attachedGM}</b>
                              </span>
                            )}
                            {(agent.role === 'General Manager' || agent.role === 'GM') && agent['attached-jm']?.length > 0 && (
                              <JMListDropdown jms={agent['attached-jm']} />
                            )}
                          </div>
                        </td>

                        {/* The reports email is set here; a user's login email comes from User Management.
                            Without a reports email the login email is shown, marked as such, since it
                            does not enrol them for reports. */}
                        <td className="px-5 py-3 whitespace-nowrap text-gray-700">
                          {agent.email ? agent.email : agent.loginEmail ? (
                            <span className="flex flex-col">
                              <span>{agent.loginEmail}</span>
                              <span className="text-[11px] text-gray-400">Login email</span>
                            </span>
                          ) : <span className="text-gray-400 italic">Not set</span>}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-500">
                          {agent.updatedAt ? new Date(agent.updatedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          {isRegistered ? (
                            <Chip tint="cloudza" className="whitespace-nowrap">Cognito User Active</Chip>
                          ) : agent.email ? (
                            // Weekly agent report emails are switched off
                            // (pca-dashboards.template), so an email no longer
                            // means reports.
                            <Chip tint="green" className="gap-1.5 whitespace-nowrap">
                              <FaEnvelope style={{ fontSize: '0.7rem' }} />
                              Email set
                            </Chip>
                          ) : (
                            <Chip tint="gray" className="gap-1.5 whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                              No email
                            </Chip>
                          )}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-right space-x-2">
                          {isTeamMode ? (
                            <TeamAction
                              agent={agent}
                              me={me.agentName}
                              busy={teamBusy.has(agent.name)}
                              onChange={handleTeamChange}
                            />
                          ) : (
                            // Always enabled: this is where an agent is made a General Manager or put on a
                            // GM's team, and agents who can sign in are exactly the ones who need that.
                            <button
                              onClick={() => setEditAgent(agent)}
                              className="btn-outline !py-1 !px-3 !text-xs"
                            >
                              <FaPen />
                              {agent.email || agent.role ? 'Edit Details' : 'Set Details'}
                            </button>
                          )}

                          {/* No Create User here (2026-10-20): users are made in User Management. */}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
          {agents.length > 0 && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <span className="text-xs text-gray-400">
                {agentFilter === 'all' || agentFilter === 'bygm'
                  ? `Showing ${agents.length.toLocaleString()}`
                  : `${visibleAgents.length.toLocaleString()} match this filter among the ${agents.length.toLocaleString()} agents loaded so far`}
              </span>
              {agentsNext && !agentsLoading && (
                <button
                  onClick={loadMoreAgents}
                  disabled={agentsLoadingMore}
                  className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {agentsLoadingMore
                    ? <><InlineSpinner className="mr-2" />Loading...</>
                    : <><FaChevronDown /> Load More Agents</>}
                </button>
              )}
            </div>
          )}
          <AgentEmailModal
            open={!!editAgent}
            agent={editAgent}
            gmUsername={generalManagers}
            // Logins that are not yet tied to any calls. One that already has a GM/JM role is still
            // offered: the server then explains that the role must be cleared before linking.
            loginOptions={linkableLogins}
            onClose={() => setEditAgent(null)}
            onSubmit={handleUpdateAgentEmail}
          />
        </>
      )}

      {/* Activity Log Tab Content */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          <div className="max-w-sm">
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">Admin</label>
            <SelectField
              className="select-field w-full"
              value={selectedActor}
              onChange={(e) => handleSelectActor(e.target.value)}
              disabled={actorsLoading}
            >
              <option value="">
                {actorsLoading ? 'Loading admins...' : 'Select an admin'}
              </option>
              {actors.map((a) => (
                <option key={a.actorEmail} value={a.actorEmail}>
                  {a.actorEmail} ({a.activityCount} action{a.activityCount === 1 ? '' : 's'})
                </option>
              ))}
            </SelectField>
          </div>

          {!actorsLoading && actors.length === 0 && (
            <div className="py-10 px-6 text-center">
              <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                <FaHistory className="text-lg" />
              </span>
              <p className="mt-4 text-sm font-semibold text-gray-800">No activity recorded yet</p>
              <p className="mt-1 text-sm text-gray-400">Entries appear here once an admin edits an agent's email or role on the "Agent Emails" tab.</p>
            </div>
          )}

          {selectedActor && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">When</th>
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Agent</th>
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Email</th>
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Role</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {activitiesLoading ? (
                    <TableRowsSkeleton columns={4} />
                  ) : activities.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-10 px-6 text-center">
                        <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                          <FaHistory className="text-lg" />
                        </span>
                        <p className="mt-4 text-sm font-semibold text-gray-800">No activity recorded for this admin</p>
                        <p className="mt-1 text-sm text-gray-400">This admin hasn't made any agent email or role changes yet.</p>
                      </td>
                    </tr>
                  ) : activities.map((entry) => (
                    <tr key={entry.id} className="hover:bg-cloudza-50/40 transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-500 text-xs">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <IdentityTile name={entry.agentName} />
                          <span className="font-semibold text-gray-900">{entry.agentName}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-gray-700">{entry.details?.email || <span className="text-gray-400 italic">removed</span>}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {/* Older entries predate before-value capture and only carry the new role. */}
                        {entry.details?.previousRole && entry.details.previousRole !== entry.details?.role ? (
                          <span className="inline-flex items-center gap-1.5">
                            <RoleBadge role={entry.details.previousRole} />
                            <span className="text-gray-400" aria-hidden="true">→</span>
                            <RoleBadge role={entry.details?.role} />
                          </span>
                        ) : (
                          <RoleBadge role={entry.details?.role} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <UserFormModal
        open={showCreate}
        title="Create User"
        groups={groups}
        initial={NEW_USER}
        onClose={() => setShowCreate(false)}
        onSubmit={async (data) => {
          try {
            await createUser({
              username: data.username,
              email: data.email,
              temporaryPassword: data.password || undefined,
              groups: data.groups
            });
            setConfirmation({ 
                    open: true, 
                    title: 'User Created', 
                    message: `User ${data.username} was created successfully.` 
                  });
            setShowCreate(false);
            loadUsers();
          } catch (err) {
            console.error('Failed to create user', err);
            // Surface the backend's reason — "Username already exists" in
            // particular — instead of a generic failure.
            const backendMessage = err?.message || '';
            const message = /exist/i.test(backendMessage)
              ? `A user named "${data.username}" already exists. Choose a different username, or update the existing user instead.`
              : backendMessage || "Couldn't create user";
            setConfirmation({ open: true, title: "Couldn't Create User", message });
          }
        }}
      />

      {editUserModal && (
        <UserFormModal
          open={!!editUserModal}
          title={`Edit ${editUserModal.Username}`}
          groups={groups}
          initial={{
            username: editUserModal.Username,
            email: editUserModal.Attributes?.find(a => a.Name === 'email')?.Value || '',
            groups: (editUserModal.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '').split(',').filter(Boolean)
          }}
          onClose={() => setEditUserModal(null)}
          onSubmit={async (data) => {
            try {
              const before = {
                email: editUserModal.Attributes?.find(a => a.Name === 'email')?.Value || '',
                groups: (editUserModal.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '').split(',').filter(Boolean),
              };
              const { emailChanged, groupsChanged } = await saveUserEdits(
                editUserModal.Username,
                before,
                { email: data.email, groups: data.groups },
                { updateUser, setUserGroups }
              );
              setEditUserModal(null);
              const changes = [
                emailChanged ? `email set to ${data.email}` : null,
                groupsChanged ? `groups set to ${data.groups.join(', ')}` : null,
              ].filter(Boolean);
              setConfirmation({
                open: true,
                title: 'User Updated',
                message: changes.length
                  ? `${editUserModal.Username}: ${changes.join('; ')}.`
                  : 'Nothing changed.',
              });
              loadUsers();
            } catch (err) {
              console.error('Failed to update user', err);
              setConfirmation({ open: true, title: "Couldn't Update User", message: err?.message || "Couldn't update user" });
              loadUsers(); // the email may have saved before the groups failed
            }
          }}
        />
      )}

      {resetUserModal && (
        <ResetPasswordModal
          open={!!resetUserModal}
          username={resetUserModal.Username}
          onClose={() => setResetUserModal(null)}
          onSubmit={async (pw) => {
            try {
              await resetUserPassword(resetUserModal.Username, pw);
              setResetUserModal(null);
              setConfirmation({ open: true, title: 'Password Reset', message: 'Temporary password set.' });
            } catch (err) {
              console.error('Failed to reset password', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't reset password" });
            }
          }}
        />
      )}

      {roleModal && (
        <RoleModal
          open={!!roleModal}
          username={roleModal.Username}
          onClose={() => setRoleModal(null)}
          onSubmit={async (role) => {
            try {
              await setUserRole(roleModal.Username, role);
              setRoleModal(null);
              setConfirmation({ open: true, title: 'Role Updated', message: 'Role updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to set role', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't set role" });
            }
          }}
        />
      )}

      {confirmAction && (
        <Modal open={!!confirmAction} onClose={() => setConfirmAction(null)} title={confirmAction.title}>
          <p className="mb-4">{confirmAction.message}</p>
          <div className="flex flex-wrap justify-end gap-2">
            <button onClick={() => setConfirmAction(null)} className="btn-outline"><FaTimes /> Cancel</button>
            <button
              onClick={async () => {
                await confirmAction.onConfirm();
              }}
              className="btn-primary"
            >
              <FaCheck />
              Confirm
            </button>
          </div>
        </Modal>
      )}
      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => setConfirmation({ open: false, title: '', message: '' })}
      />
      <DeleteConfirmationModal
        open={!!deleteUserName}
        itemName={deleteUserName || ''}
        onClose={() => setDeleteUserName('')}
        onConfirm={confirmDeleteUser}
      />
    </div>
  );
};

// The /agents route's page — same component, agent-facing section.
export const AgentManagement = (props) => <UserManagement {...props} section="agents" />;

export default UserManagement;
